import { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  BrowserRouter,
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';

import type { SiteContent } from '@saas-bot/types';

import { fetchSiteContent } from './lib/api';
import { captureAttribution } from './lib/forms';
import { MarketingHome } from './marketing/MarketingHome';
import { MotionProvider } from './motion/MotionProvider';
import { BlogList, BlogPostView } from './site/Blog';
import { FALLBACK_CONTENT } from './site/fallback';
import { Layout } from './site/Layout';
import { SiteIntro } from './site/SiteIntro';
import { PageView } from './site/Page';
import { Container } from './site/ui';
import './styles.css';

/**
 * The site now renders on a fixed dark palette (see styles.css), so the CMS
 * `accentColor` / `backgroundColor` settings are intentionally NOT applied:
 * the authored values are a deep violet on warm cream and are unreadable on
 * black. The settings are left in the CMS untouched — re-enable this if a
 * light theme ever comes back.
 */
function applyTheme(_content: SiteContent): void {
  /* no-op — palette is fixed in styles.css */
}

function useSiteContent(): SiteContent {
  // Start from the built-in content so the hero headline and CTA paint on the
  // first frame — no spinner in front of LCP — then swap to live CMS content
  // when the API responds.
  const [content, setContent] = useState<SiteContent>(FALLBACK_CONTENT);

  useEffect(() => {
    captureAttribution();
    applyTheme(FALLBACK_CONTENT);
    const controller = new AbortController();
    void fetchSiteContent(controller.signal).then((result) => {
      if (!result) return;
      applyTheme(result);
      setContent(result);
    });
    return () => controller.abort();
  }, []);

  return content;
}

function LoadingScreen(): JSX.Element {
  return (
    <div className="grid min-h-dvh place-items-center">
      <span
        className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--color-line)] border-t-[var(--accent)]"
        aria-label="Loading"
      />
    </div>
  );
}

function NotFound(): JSX.Element {
  return (
    <section className="py-32">
      <Container className="flex flex-col items-center gap-5 text-center">
        <p className="accent-text font-[var(--font-display)] text-6xl font-bold">404</p>
        <h1 className="text-2xl font-semibold text-[var(--color-ink)]">This page moved on.</h1>
        <p className="max-w-md text-[var(--color-ink-soft)]">
          The page you were looking for is not here. Let’s get you back to solid ground.
        </p>
        <Link
          to="/"
          className="accent-bg mt-2 inline-flex items-center rounded-full px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
        >
          Back home
        </Link>
      </Container>
    </section>
  );
}

function ResolvedPage({ content }: { content: SiteContent }): JSX.Element {
  const { slug } = useParams();
  const location = useLocation();

  // A managed redirect wins before we try to resolve a page. Internal targets go
  // through the router; external ones leave the SPA entirely.
  const redirect = content.redirects.find((rule) => rule.sourcePath === location.pathname);
  if (redirect) {
    if (redirect.destination.startsWith('/')) return <Navigate to={redirect.destination} replace />;
    window.location.replace(redirect.destination);
    return <LoadingScreen />;
  }

  const target = slug ?? 'home';
  const page = content.pages.find((candidate) => candidate.slug === target);
  if (!page) return <NotFound />;
  return <PageView page={page} settings={content.settings} />;
}

/**
 * The homepage is the hand-built marketing experience, which brings its own
 * navigation and footer — so it renders outside the CMS chrome. A managed
 * redirect on "/" still wins, and the CMS-authored home page stays reachable at
 * its own slug (/home).
 */
function HomeRoute({ content }: { content: SiteContent }): JSX.Element {
  const redirect = content.redirects.find((rule) => rule.sourcePath === '/');
  if (redirect) {
    if (redirect.destination.startsWith('/')) return <Navigate to={redirect.destination} replace />;
    window.location.replace(redirect.destination);
    return <LoadingScreen />;
  }
  return <MarketingHome />;
}

/** Wraps the CMS-driven routes in the shared light-theme site chrome. */
function ChromeRoutes({ content }: { content: SiteContent }): JSX.Element {
  return (
    <Layout settings={content.settings}>
      <Routes>
        <Route path="/blog" element={<BlogList settings={content.settings} />} />
        <Route path="/blog/:postSlug" element={<BlogPostView settings={content.settings} />} />
        <Route path="/:slug" element={<ResolvedPage content={content} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function App(): JSX.Element {
  const content = useSiteContent();

  return (
    <MotionProvider>
      <SiteIntro brandName={content.settings.brandName} logoUrl="/icons/vaanexa-icon.svg" />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomeRoute content={content} />} />
          <Route path="*" element={<ChromeRoutes content={content} />} />
        </Routes>
      </BrowserRouter>
    </MotionProvider>
  );
}

createRoot(document.getElementById('root')!).render(<App />);
